import { SceneData } from '@/types/course.types';

interface ScenePreviewCardProps {
  data: SceneData;
  theme?: string;
}

const THEME_COLORS: Record<string, { primary: string; secondary: string; bg: string; surface: string; text: string; muted: string; accent: string }> = {
  educational: { primary: '#6366f1', secondary: '#8b5cf6', bg: '#0f172a', surface: 'rgba(255,255,255,0.06)', text: '#f1f5f9', muted: '#94a3b8', accent: '#a78bfa' },
  modern: { primary: '#3b82f6', secondary: '#06b6d4', bg: '#0c0a1a', surface: 'rgba(255,255,255,0.05)', text: '#e2e8f0', muted: '#94a3b8', accent: '#38bdf8' },
  corporate: { primary: '#0369a1', secondary: '#0284c7', bg: '#0f1729', surface: 'rgba(255,255,255,0.05)', text: '#e2e8f0', muted: '#94a3b8', accent: '#38bdf8' },
  dark: { primary: '#a78bfa', secondary: '#c084fc', bg: '#09090b', surface: 'rgba(255,255,255,0.04)', text: '#e4e4e7', muted: '#71717a', accent: '#c084fc' },
  minimal: { primary: '#6366f1', secondary: '#8b5cf6', bg: '#fafafa', surface: 'rgba(0,0,0,0.03)', text: '#1e293b', muted: '#64748b', accent: '#6366f1' },
};

export default function ScenePreviewCard({ data, theme = 'educational' }: ScenePreviewCardProps) {
  const colors = THEME_COLORS[theme] || THEME_COLORS.educational;
  const is_light = theme === 'minimal';

  const base_style: React.CSSProperties = {
    width: '100%',
    aspectRatio: '16 / 9',
    background: colors.bg,
    color: colors.text,
    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '5% 6%',
    fontSize: 'clamp(8px, 1.2vw, 14px)',
    lineHeight: 1.5,
  };

  const heading_style: React.CSSProperties = {
    fontSize: 'clamp(12px, 2vw, 22px)',
    fontWeight: 700,
    marginBottom: '0.5em',
    lineHeight: 1.2,
    background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  };

  const tag_style: React.CSSProperties = {
    fontSize: 'clamp(6px, 0.7vw, 9px)',
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: colors.accent,
    textTransform: 'uppercase' as const,
    marginBottom: '0.4em',
  };

  const surface_style: React.CSSProperties = {
    background: colors.surface,
    borderRadius: 6,
    padding: '3% 4%',
    border: `1px solid ${is_light ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)'}`,
  };

  const callout_style: React.CSSProperties = {
    marginTop: '0.8em',
    padding: '2.5% 3.5%',
    background: `linear-gradient(135deg, ${colors.primary}18, ${colors.secondary}10)`,
    borderLeft: `3px solid ${colors.primary}`,
    borderRadius: 4,
    fontSize: 'clamp(7px, 0.9vw, 11px)',
    color: colors.accent,
    fontStyle: 'italic',
  };

  const empty_hint = (field: string) => (
    <span style={{ color: colors.muted, fontStyle: 'italic', opacity: 0.5, fontSize: 'clamp(7px, 0.8vw, 10px)' }}>
      {field} (empty)
    </span>
  );

  const renderTag = () => {
    if (!data.tag_label) return null;
    return <div style={tag_style}>{data.icon ? `${data.icon} ` : ''}{data.tag_label}</div>;
  };

  const renderCallout = () => {
    if (!data.callout_text) return null;
    return <div style={callout_style}>{data.callout_text}</div>;
  };

  const renderHeading = () => (
    <div style={heading_style}>{data.heading || empty_hint('Heading')}</div>
  );

  const renderLayout = () => {
    switch (data.layout_type) {
      case 'title':
        return (
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            {renderTag()}
            <div style={{ ...heading_style, fontSize: 'clamp(16px, 2.8vw, 28px)' }}>
              {data.heading || empty_hint('Title')}
            </div>
            {data.subheading ? (
              <div style={{ color: colors.muted, fontSize: 'clamp(9px, 1.2vw, 14px)', marginTop: '0.3em' }}>{data.subheading}</div>
            ) : empty_hint('Subheading')}
          </div>
        );

      case 'content':
        return (
          <>
            {renderTag()}
            {renderHeading()}
            <div style={surface_style}>
              {data.body_text ? (
                <p style={{ color: colors.muted }}>{data.body_text}</p>
              ) : empty_hint('Body text')}
            </div>
            {renderCallout()}
          </>
        );

      case 'bullet':
        return (
          <>
            {renderTag()}
            {renderHeading()}
            <div style={surface_style}>
              {data.bullet_points && data.bullet_points.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: '1.2em', listStyleType: 'disc' }}>
                  {data.bullet_points.map((bp, i) => (
                    <li key={i} style={{ color: colors.muted, marginBottom: '0.3em' }}>
                      <span style={{ color: colors.text }}>{bp || empty_hint(`Point ${i + 1}`)}</span>
                    </li>
                  ))}
                </ul>
              ) : empty_hint('Bullet points')}
            </div>
            {renderCallout()}
          </>
        );

      case 'code':
        return (
          <>
            {renderTag()}
            {renderHeading()}
            <div style={{
              ...surface_style,
              fontFamily: "'Fira Code', 'Cascadia Code', 'JetBrains Mono', monospace",
              fontSize: 'clamp(7px, 0.85vw, 11px)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              overflowY: 'auto',
              maxHeight: '55%',
              background: is_light ? '#1e293b' : 'rgba(0,0,0,0.4)',
              color: is_light ? '#e2e8f0' : '#a5f3fc',
            }}>
              {data.code_snippet?.language && (
                <div style={{ fontSize: 'clamp(6px, 0.6vw, 8px)', color: '#64748b', marginBottom: '0.4em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {data.code_snippet.language}
                </div>
              )}
              {data.code_snippet?.code || empty_hint('Code snippet')}
            </div>
            {renderCallout()}
          </>
        );

      case 'comparison':
        return (
          <>
            {renderTag()}
            {renderHeading()}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3%' }}>
              {(['left', 'right'] as const).map((side) => {
                const col = data.comparison?.[side];
                return (
                  <div key={side} style={{ ...surface_style, borderColor: side === 'left' ? colors.primary + '40' : colors.secondary + '40' }}>
                    <div style={{ fontWeight: 700, fontSize: 'clamp(8px, 1vw, 12px)', color: side === 'left' ? colors.primary : colors.secondary, marginBottom: '0.4em' }}>
                      {col?.title || empty_hint(`${side} title`)}
                    </div>
                    {col?.points && col.points.length > 0 ? (
                      <ul style={{ margin: 0, paddingLeft: '1em', listStyleType: 'disc' }}>
                        {col.points.map((p, i) => (
                          <li key={i} style={{ color: colors.muted, fontSize: 'clamp(7px, 0.8vw, 10px)', marginBottom: '0.2em' }}>{p}</li>
                        ))}
                      </ul>
                    ) : empty_hint('Points')}
                  </div>
                );
              })}
            </div>
          </>
        );

      case 'image_text':
        return (
          <>
            {renderTag()}
            {renderHeading()}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3%', alignItems: 'center' }}>
              <div style={{ ...surface_style, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60px', color: colors.muted }}>
                {data.image_urls && data.image_urls[0] ? (
                  <img src={data.image_urls[0]} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 4 }} />
                ) : (
                  <span style={{ fontSize: 'clamp(7px, 0.8vw, 10px)', opacity: 0.5 }}>Image placeholder</span>
                )}
              </div>
              <div style={{ color: colors.muted }}>
                {data.body_text || empty_hint('Body text')}
              </div>
            </div>
          </>
        );

      case 'summary':
        return (
          <>
            {renderTag()}
            {renderHeading()}
            <div style={surface_style}>
              {data.key_takeaways && data.key_takeaways.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4em' }}>
                  {data.key_takeaways.map((t, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5em' }}>
                      <span style={{ color: '#22c55e', fontWeight: 700, flexShrink: 0 }}>&#10003;</span>
                      <span style={{ color: colors.text }}>{t}</span>
                    </div>
                  ))}
                </div>
              ) : empty_hint('Key takeaways')}
            </div>
          </>
        );

      case 'qa':
        return (
          <>
            {renderTag()}
            {renderHeading()}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3%' }}>
              <div style={{ ...surface_style, borderLeft: `3px solid ${colors.primary}` }}>
                <div style={{ fontSize: 'clamp(6px, 0.65vw, 8px)', fontWeight: 700, color: colors.primary, marginBottom: '0.3em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Question</div>
                {data.question || empty_hint('Question')}
              </div>
              <div style={{ ...surface_style, borderLeft: `3px solid #22c55e` }}>
                <div style={{ fontSize: 'clamp(6px, 0.65vw, 8px)', fontWeight: 700, color: '#22c55e', marginBottom: '0.3em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Answer</div>
                {data.answer || empty_hint('Answer')}
              </div>
            </div>
          </>
        );

      case 'timeline':
      case 'process': {
        const steps = data.steps || [];
        return (
          <>
            {renderTag()}
            {renderHeading()}
            {steps.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '2%', overflowX: 'auto' }}>
                {steps.map((step, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1.5%', flex: '1 0 auto', minWidth: 0 }}>
                    <div style={{
                      ...surface_style,
                      flex: '1 1 auto',
                      minWidth: 0,
                      textAlign: 'center',
                      position: 'relative',
                    }}>
                      <div style={{
                        width: 'clamp(14px, 2vw, 22px)',
                        height: 'clamp(14px, 2vw, 22px)',
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 'clamp(6px, 0.7vw, 10px)',
                        fontWeight: 700,
                        margin: '0 auto 0.3em',
                      }}>
                        {i + 1}
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 'clamp(7px, 0.85vw, 10px)', color: colors.text, wordBreak: 'break-word' }}>
                        {step.title || empty_hint(`Step ${i + 1}`)}
                      </div>
                      {data.layout_type === 'process' && step.description && (
                        <div style={{ fontSize: 'clamp(6px, 0.65vw, 8px)', color: colors.muted, marginTop: '0.2em' }}>{step.description}</div>
                      )}
                    </div>
                    {i < steps.length - 1 && (
                      <span style={{ color: colors.muted, fontSize: 'clamp(8px, 1vw, 14px)', flexShrink: 0 }}>&#8594;</span>
                    )}
                  </div>
                ))}
              </div>
            ) : empty_hint('Steps')}
          </>
        );
      }

      case 'stats': {
        const stats = data.stats || [];
        return (
          <>
            {renderTag()}
            {renderHeading()}
            {stats.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(stats.length, 4)}, 1fr)`, gap: '3%' }}>
                {stats.map((stat, i) => (
                  <div key={i} style={{ ...surface_style, textAlign: 'center' }}>
                    <div style={{
                      fontSize: 'clamp(14px, 2.2vw, 24px)',
                      fontWeight: 800,
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      {stat.value || '—'}
                    </div>
                    <div style={{ fontSize: 'clamp(6px, 0.7vw, 9px)', color: colors.muted, marginTop: '0.2em' }}>
                      {stat.label || empty_hint('Label')}
                    </div>
                  </div>
                ))}
              </div>
            ) : empty_hint('Stats')}
          </>
        );
      }

      case 'quote':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
            {renderTag()}
            <div style={{ fontSize: 'clamp(20px, 3.5vw, 40px)', color: colors.primary, opacity: 0.3, lineHeight: 1 }}>&ldquo;</div>
            <div style={{
              fontSize: 'clamp(10px, 1.5vw, 18px)',
              fontStyle: 'italic',
              color: colors.text,
              maxWidth: '80%',
              lineHeight: 1.6,
              margin: '0.3em 0',
            }}>
              {data.quote_text || empty_hint('Quote text')}
            </div>
            {data.attribution && (
              <div style={{ fontSize: 'clamp(7px, 0.8vw, 10px)', color: colors.muted, marginTop: '0.5em' }}>
                {data.attribution}
              </div>
            )}
          </div>
        );

      default:
        return (
          <>
            {renderTag()}
            {renderHeading()}
            <div style={{ color: colors.muted, fontStyle: 'italic' }}>
              Unknown layout: {data.layout_type}
            </div>
          </>
        );
    }
  };

  return (
    <div style={base_style}>
      {renderLayout()}
    </div>
  );
}
